'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import type { HeroModel } from '@/lib/mappers/heroMapper';
import type { CategoriesModel } from '@/lib/mappers/categoriesMapper';
import { useCategory } from '@/contexts/CategoryContext';

interface HeroNewProps {
  initialData: HeroModel;
  categoriesData: CategoriesModel;
  dict: any;
}

// Wisconsin major cities and counties
const WISCONSIN_CITIES = [
  'Milwaukee',
  'Madison',
  'Green Bay',
  'Kenosha',
  'Racine',
  'Appleton',
  'Waukesha',
  'Eau Claire',
  'Oshkosh',
  'Janesville',
  'West Allis',
  'La Crosse',
  'Sheboygan',
  'Wauwatosa',
  'Fond du Lac',
  'New Berlin',
  'Wausau',
  'Brookfield',
  'Greenfield',
  'Beloit',
];

const HeroNew = ({ initialData, categoriesData, dict }: HeroNewProps) => {
  const router = useRouter();
  const { selectedCategory, setSelectedCategory } = useCategory();
  const [searchData, setSearchData] = useState({
    keyword: '',
    zipCode: '',
    cityCounty: ''
  });
  const [isSearching, setIsSearching] = useState(false);

  // Helper function to split title and wrap last word in span for orange color
  const formatTitle = (title: string) => {
    const words = title.trim().split(' ');
    if (words.length === 1) {
      return <span>{title}</span>;
    }
    const lastWord = words.pop();
    const restOfTitle = words.join(' ');
    return (
      <>
        {restOfTitle} <span>{lastWord}</span>
      </>
    );
  };
  
  // Store setSelectedCategory in a ref so jQuery can access it
  const setSelectedCategoryRef = useRef(setSelectedCategory);
  const categoriesDataRef = useRef(categoriesData);
  
  useEffect(() => {
    setSelectedCategoryRef.current = setSelectedCategory;
    categoriesDataRef.current = categoriesData;
  }, [setSelectedCategory, categoriesData]);

  // Function to update category - accessible to jQuery
  const updateCategoryByIndex = (index: number) => {
    console.log('🔄 Updating category to index:', index);
    const categories = categoriesDataRef.current.categories;
    if (categories[index]) {
      const category = categories[index];
      console.log('✅ Category found:', category.title);
      setSelectedCategoryRef.current({
        id: category.id,
        title: category.title,
        description: category.description || '',
        media: category.video
          ? { type: 'video', src: category.video, poster: category.image?.src, alt: category.image?.alt }
          : { type: 'image', src: category.image?.src || '', alt: category.image?.alt },
        color: category.color,
      });
    } else {
      console.log('❌ No category found at index:', index);
    }
  };

  // Store updateCategoryByIndex on window for jQuery to access
  useEffect(() => {
    (window as any).updateHeroCategory = updateCategoryByIndex;
    return () => {
      delete (window as any).updateHeroCategory;
    };
  }, []);

  // Initialize Owl Carousel on mount
  useEffect(() => {
    // Wait for jQuery and Owl Carousel to load
    const initCarousels = () => {
      if (typeof window !== 'undefined' && (window as any).$) {
        const $ = (window as any).$;
        
        // Initialize main slider
        const mainSlider = $('.main-slider');
        const thumbSlider = $('.thumb-slider');
        
        if (mainSlider.length && !mainSlider.hasClass('owl-loaded')) {
          mainSlider.owlCarousel({
            items: 1,
            loop: true,
            autoplay: false,
            nav: false,
            dots: false,
            animateOut: 'fadeOut',
            animateIn: 'fadeIn',
          });
        }
        
        // Initialize thumbnail slider
        if (thumbSlider.length && !thumbSlider.hasClass('owl-loaded')) {
          thumbSlider.owlCarousel({
            items: 5,
            loop: false,
            margin: 10,
            nav: false,
            dots: false,
            responsive: {
              0: { items: 2 },
              600: { items: 3 },
              1000: { items: 5 }
            }
          });
          
          // Sync thumbnail click with main slider and update category
          thumbSlider.find('.thumb').on('click', function(this: any) {
            const index = $(this).parent().index();
            console.log('👆 Thumbnail clicked, index:', index);
            mainSlider.trigger('to.owl.carousel', [index, 300]);
            
            // Update active thumbnail
            thumbSlider.find('.thumb').removeClass('active-thumb');
            $(this).addClass('active-thumb');
            
            // Update selected category using window function
            if ((window as any).updateHeroCategory) {
              console.log('🔄 Calling updateHeroCategory with index:', index);
              (window as any).updateHeroCategory(index);
            } else {
              console.log('❌ updateHeroCategory not found on window');
            }
          });
          
          // Set first thumbnail as active
          thumbSlider.find('.thumb').first().addClass('active-thumb');
          
          // Sync thumbnails with main slider change
          mainSlider.on('changed.owl.carousel', function(e: any) {
            const index = e.item.index;
            console.log('📸 Main slider changed to index:', index);
            thumbSlider.find('.thumb').removeClass('active-thumb');
            thumbSlider.find('.thumb').eq(index).addClass('active-thumb');
            
            // Update selected category using window function
            if ((window as any).updateHeroCategory) {
              console.log('🔄 Calling updateHeroCategory from slider change, index:', index);
              (window as any).updateHeroCategory(index);
            }
          });
        }
      }
    };

    // Try to initialize immediately
    initCarousels();
    
    // Also try after a short delay to ensure scripts are loaded
    const timer = setTimeout(initCarousels, 100);
    
    return () => {
      clearTimeout(timer);
      // Cleanup carousels
      if (typeof window !== 'undefined' && (window as any).$) {
        const $ = (window as any).$;
        $('.main-slider').trigger('destroy.owl.carousel');
        $('.thumb-slider').trigger('destroy.owl.carousel');
      }
    };
  }, []);

  // Auto-select first category on load
  useEffect(() => {
    if (categoriesData.categories.length > 0 && !selectedCategory) {
      const firstCategory = categoriesData.categories[0];
      setSelectedCategory({
        id: firstCategory.id,
        title: firstCategory.title,
        description: firstCategory.description || '',
        media: firstCategory.video
          ? { type: 'video', src: firstCategory.video, poster: firstCategory.image?.src, alt: firstCategory.image?.alt }
          : { type: 'image', src: firstCategory.image?.src || '', alt: firstCategory.image?.alt },
        color: firstCategory.color,
      });
    }
  }, [categoriesData.categories, selectedCategory, setSelectedCategory]);

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set loading state
    setIsSearching(true);
    
    // Build URL parameters for find-services page
    const params = new URLSearchParams();
    
    if (searchData.keyword.trim()) {
      params.set('query', searchData.keyword.trim());
    }
    
    if (searchData.cityCounty.trim()) {
      params.set('city[0]', searchData.cityCounty.trim());
    }
    
    if (searchData.zipCode.trim()) {
      params.set('zip[0]', searchData.zipCode.trim());
    }
    
    // Add a delay before navigation for better UX
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Navigate to find-services page with parameters
    const searchUrl = `/en/find-services${params.toString() ? `?${params.toString()}` : ''}`;
    router.push(searchUrl);
    
    // Keep loading state until page navigates
    // It will be reset when component unmounts
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setSearchData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get banner media (images/videos) from categories or use default
  const bannerMedia = categoriesData.categories.length > 0
    ? categoriesData.categories.slice(0, 5).map(cat => ({
        type: cat.video ? 'video' as const : 'image' as const,
        src: cat.video || cat.image.src,
        poster: cat.image.src,
        alt: cat.image.alt || cat.title,
        description: cat.description || ''
      }))
    : [
        { type: 'image' as const, src: '/images/banner/banner-bg-1.jpg', poster: '/images/banner/banner-bg-1.jpg', alt: 'Banner 1', description: '' },
        { type: 'image' as const, src: '/images/banner/banner-bg-2.jpg', poster: '/images/banner/banner-bg-2.jpg', alt: 'Banner 2', description: '' },
        { type: 'image' as const, src: '/images/banner/banner-bg-3.jpg', poster: '/images/banner/banner-bg-3.jpg', alt: 'Banner 3', description: '' },
        { type: 'image' as const, src: '/images/banner/banner-bg-4.jpg', poster: '/images/banner/banner-bg-4.jpg', alt: 'Banner 4', description: '' },
        { type: 'image' as const, src: '/images/banner/banner-bg-5.jpg', poster: '/images/banner/banner-bg-5.jpg', alt: 'Banner 5', description: '' },
      ];

  return (
    <section className="hero-banner pt-0 bdm">
      <div className="banner-box">
        <div className="bg-slider">
          <div className="main-slider owl-carousel owl-theme">
            {bannerMedia.map((media, index) => (
              <div className="item" key={index}>
                {media.type === 'video' ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={media.poster}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  >
                    <source src={media.src} type="video/mp4" />
                  </video>
                ) : (
                  <img src={media.src} alt={media.alt} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="container">
          <div className="row align-items-center justify-content-between">
            <div className="col-lg-6">
              <div className="banner-content">
                <h1>
                  {formatTitle(selectedCategory?.title || initialData.title || 'Guiding You to HealthyAging and Supportive Care')}
                </h1>
                {/* <p>
                  {selectedCategory?.description  || 'VirginiaNavigator is your trusted guide, connecting seniors, caregivers, veterans, and people with disabilities to health, housing, transportation, and caregiving resources.'}
                </p> */}
                <a href="#" className="btn btn-secondary">
                  <img src="/images/icons/right_arrow.svg" className="arrow-icon" alt="" />
                  Read More
                </a>
              </div>
            </div>
            <div className="col-lg-5 d-none d-lg-block">
              <div className="find-box">
                <h4>Find the Services You Need</h4>
                <form onSubmit={handleSearchSubmit}>
                  <div className="sip-box">
                    <label htmlFor="keyword">
                      <img src="/images/icons/keyword_search.svg" alt="" />
                    </label>
                    <input 
                      type="search" 
                      className="form-control" 
                      placeholder="Search by keyword"
                      name="keyword"
                      value={searchData.keyword}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="sip-box">
                    <label htmlFor="zipCode">
                      <img src="/images/icons/location_icon.svg" alt="" />
                    </label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Zip Code"
                      name="zipCode"
                      value={searchData.zipCode}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="sip-box">
                    <label htmlFor="cityCounty">
                      <img src="/images/icons/location_icon.svg" alt="" />
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="City or County"
                      name="cityCounty"
                      value={searchData.cityCounty}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button type="submit" className="btn btn-secondary" disabled={isSearching}>
                    {isSearching ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Slider */}
      <div className="thumb-slider owl-carousel owl-theme">
        {bannerMedia.map((media, index) => (
          <div className="thumb" key={index}>
            <div className="img-box" style={{ position: 'relative' }}>
              {media.type === 'video' ? (
                <img src={media.poster} alt={media.alt} />
              ) : (
                <img src={media.src} alt={media.alt} />
              )}
              {media.description && (
                <div 
                  className="thumb-description" 
                  style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)',
                    color: '#fff',
                    padding: '16px',
                    fontSize: '20px',
                    fontWeight: '900',
                    marginBottom: '0',
                    lineHeight: '1.5',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box'
                    
                  }}
                >
                  <span
                    style={{
                      maxWidth: '90%',
                      wordBreak: 'break-word'
                    }}
                  >
                    {media.description}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Search Form */}
      <div className="mt-5 d-block d-lg-none">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="find-box">
                <h4>Find the Services You Need</h4>
                <form onSubmit={handleSearchSubmit}>
                  <div className="sip-box">
                    <label htmlFor="keyword-mobile">
                      <img src="/images/icons/keyword_search.svg" alt="" />
                    </label>
                    <input 
                      type="search" 
                      className="form-control" 
                      placeholder="Search by keyword"
                      name="keyword"
                      value={searchData.keyword}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="sip-box">
                    <label htmlFor="zipCode-mobile">
                      <img src="/images/icons/location_icon.svg" alt="" />
                    </label>
                    <input 
                      type="text" 
                      className="form-control" 
                      placeholder="Zip Code"
                      name="zipCode"
                      value={searchData.zipCode}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="sip-box">
                    <label htmlFor="cityCounty-mobile">
                      <img src="/images/icons/location_icon.svg" alt="" />
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="City or County"
                      name="cityCounty"
                      value={searchData.cityCounty}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button type="submit" className="btn btn-secondary" disabled={isSearching}>
                    {isSearching ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroNew;

